import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import SettingsEthernetOutlinedIcon from '@mui/icons-material/SettingsEthernetOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from 'api/client';

const borderColor = 'rgba(245, 158, 11, 0.35)';
const cardSx = { border: '1px solid', borderColor, borderRadius: 3, boxShadow: 'none' };
const metricCardSx = {
  ...cardSx,
  background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(255,255,255,0.02))',
  minHeight: 112
};
const actionButtonSx = {
  bgcolor: 'var(--fz-surface-1, #111827)',
  color: '#f59e0b',
  border: '1px solid #f59e0b',
  '&:hover': { bgcolor: 'var(--fz-surface-0, #0b1220)', borderColor: '#f59e0b' }
};

const statusChip = (status) => {
  if (status === 'running') {
    return <Chip size="small" label="Running" sx={{ bgcolor: 'rgba(59,130,246,0.12)', color: '#1d4ed8', fontWeight: 600 }} />;
  }
  if (status === 'pending') {
    return <Chip size="small" label="Pending" sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#92400e', fontWeight: 600 }} />;
  }
  if (status === 'completed') {
    return <Chip size="small" label="Completed" sx={{ bgcolor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 600 }} />;
  }
  if (status === 'failed') {
    return <Chip size="small" label="Failed" sx={{ bgcolor: 'rgba(220,38,38,0.12)', color: '#991b1b', fontWeight: 600 }} />;
  }
  return <Chip size="small" label={status || 'Unknown'} />;
};

const formatAge = (seconds) => {
  const value = Number(seconds || 0);
  if (!value) return '0s';
  if (value < 60) return `${Math.floor(value)}s`;
  if (value < 3600) return `${Math.floor(value / 60)}m`;
  if (value < 86400) return `${Math.floor(value / 3600)}h`;
  return `${Math.floor(value / 86400)}d`;
};

const QueueMetric = ({ label, value, tone = '#16a34a', detail }) => (
  <Card sx={metricCardSx}>
    <CardContent>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, color: tone, mt: 0.5 }}>
        {value}
      </Typography>
      {detail && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {detail}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const AdminSystemJobsPage = () => {
  const [rows, setRows] = useState([]);
  const [workers, setWorkers] = useState(0);
  const [queue, setQueue] = useState({ active: 0, reserved: 0, scheduled: 0, workers: [] });
  const [backlog, setBacklog] = useState({ pending: 0, running: 0, failed: 0, completed: 0, cancelled: 0, oldest_pending_age_seconds: 0 });
  const [scheduleSummary, setScheduleSummary] = useState({ total: 0, enabled: 0, due_now: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await api.get(`/api/admin/system/jobs/${params.toString() ? `?${params.toString()}` : ''}`);
      setRows(data?.results || []);
      setWorkers(data?.workers_online || 0);
      setQueue(data?.queue || { active: 0, reserved: 0, scheduled: 0, workers: [] });
      setBacklog(data?.backlog || { pending: 0, running: 0, failed: 0, completed: 0, cancelled: 0, oldest_pending_age_seconds: 0 });
      setScheduleSummary(data?.schedule_summary || { total: 0, enabled: 0, due_now: 0 });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (scanId, actionName) => {
    try {
      await api.post('/api/admin/system/jobs/', { scan_id: scanId, action: actionName });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || `Failed to ${actionName} scan.`);
    }
  };

  const runGlobalAction = async (actionName) => {
    try {
      await api.post('/api/admin/system/jobs/', { action: actionName });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || `Failed to run ${actionName}.`);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Card sx={cardSx}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={1.5}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b', width: 42, height: 42 }}>
                <SettingsEthernetOutlinedIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Job Queue
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inspect, retry, and cancel asynchronous scanning jobs.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" sx={actionButtonSx}>
                Workers: {workers}
              </Button>
              <Button variant="contained" sx={actionButtonSx} onClick={() => runGlobalAction('dispatch_schedules')}>
                Dispatch Due ({scheduleSummary.due_now || 0})
              </Button>
              <Button variant="contained" sx={actionButtonSx} onClick={() => runGlobalAction('refresh_fingerprints')}>
                Refresh Dedup
              </Button>
              <Button variant="contained" sx={actionButtonSx} onClick={() => runGlobalAction('notify_overdue_cases')}>
                Notify Overdue
              </Button>
              <Button variant="contained" sx={actionButtonSx} onClick={() => runGlobalAction('run_case_automation')}>
                Run Case Automation
              </Button>
              <Tooltip title="Refresh">
                <span>
                  <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid', borderColor }}>
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>


      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <QueueMetric label="Workers Online" value={workers} detail={`${queue.workers?.length || 0} known workers`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <QueueMetric label="Active Tasks" value={queue.active || 0} tone="#0284c7" detail="Currently executing" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <QueueMetric label="Reserved Tasks" value={queue.reserved || 0} tone="#b45309" detail="Accepted by workers" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <QueueMetric label="Scheduled Tasks" value={queue.scheduled || 0} tone="#7c3aed" detail="Delayed or ETA tasks" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <QueueMetric label="Pending Scans" value={backlog.pending || 0} tone="#f59e0b" detail={`Oldest wait ${formatAge(backlog.oldest_pending_age_seconds)}`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <QueueMetric label="Running Scans" value={backlog.running || 0} tone="#0284c7" detail="Marked running in database" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <QueueMetric label="Failed Scans" value={backlog.failed || 0} tone="#dc2626" detail="Needs review or retry" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <QueueMetric label="Completed Scans" value={backlog.completed || 0} tone="#16a34a" detail={`${backlog.cancelled || 0} cancelled`} />
        </Grid>
      </Grid>

      <Card sx={cardSx}>
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Worker Distribution
            </Typography>
            {queue.error && <Alert severity="warning">Celery inspect error: {queue.error}</Alert>}
            {(queue.workers || []).length === 0 ? (
              <Alert severity="warning">No Celery workers responded to inspect.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'rgba(245,158,11,0.08)' }}>
                      <TableCell>Worker</TableCell>
                      <TableCell align="right">Active</TableCell>
                      <TableCell align="right">Reserved</TableCell>
                      <TableCell align="right">Scheduled</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(queue.workers || []).map((worker) => (
                      <TableRow key={worker} hover>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{worker}</TableCell>
                        <TableCell align="right">{queue.active_by_worker?.[worker] || 0}</TableCell>
                        <TableCell align="right">{queue.reserved_by_worker?.[worker] || 0}</TableCell>
                        <TableCell align="right">{queue.scheduled_by_worker?.[worker] || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent>
          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Chip
                label={`Schedules: ${scheduleSummary.total || 0}`}
                sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#92400e', fontWeight: 600 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Chip
                label={`Enabled: ${scheduleSummary.enabled || 0}`}
                sx={{ bgcolor: 'rgba(22,163,74,0.12)', color: '#166534', fontWeight: 600 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Chip
                label={`Due now: ${scheduleSummary.due_now || 0}`}
                sx={{ bgcolor: 'rgba(220,38,38,0.12)', color: '#991b1b', fontWeight: 600 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="jobs-status-filter">Status</InputLabel>
                <Select
                  labelId="jobs-status-filter"
                  value={statusFilter}
                  label="Status"
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="running">Running</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={cardSx}>
        <CardContent sx={{ p: 0 }}>
          {loading && <LinearProgress />}
          {error && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(245,158,11,0.08)' }}>
                  <TableCell>Scan ID</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Alert severity="info" sx={{ my: 1 }}>
                        No jobs found.
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{String(row.id).slice(0, 8)}</TableCell>
                      <TableCell>{row.owner_email || '-'}</TableCell>
                      <TableCell>{row.target_value || '-'}</TableCell>
                      <TableCell>{row.scan_type}</TableCell>
                      <TableCell>{statusChip(row.status)}</TableCell>
                      <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Retry">
                          <span>
                            <IconButton size="small" onClick={() => runAction(row.id, 'retry')} disabled={row.status === 'running'}>
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => runAction(row.id, 'cancel')}
                              disabled={row.status === 'completed' || row.status === 'failed'}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default AdminSystemJobsPage;
